import { useEffect, useRef, useState } from 'react';

import { triggerPostMoveFlash } from '@atlaskit/pragmatic-drag-and-drop-flourish/trigger-post-move-flash';
import {
	extractInstruction,
	type Instruction,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/list-item';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { type ItemData, type MenuItemReorderOperation, OPERATIONS } from '../dnd-data';

export type UseDropTarget<T extends ItemData> = (args: {
	canDrop: (data: T) => data is T;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	isDraggableItem: (data: T) => boolean;
	performDropOperation: (
		operation: MenuItemReorderOperation,
		dragging: T,
		dropTargetData: T,
	) => void;
}) => {
	ref: React.RefObject<HTMLDivElement>;
	state: 'idle' | 'is-over' | undefined;
};

export type AnalyticsEventHandler = (args: {
	action: DragAndDropAnalyticsEventAction;
	itemId: string;
	operation?: MenuItemReorderOperation;
	successfulReorder?: boolean;
}) => void;

export type DragAndDropAnalyticsEventAction =
	| 'updated'
	| 'dragStarted'
	| 'dragLeft'
	| 'dragEntered'
	| 'dragEnded';

interface DropTarget<T> {
	ref: React.RefObject<HTMLDivElement>;
	state: 'idle' | 'is-over' | undefined;
}

export function useDropTarget<T extends ItemData>({
	canDrop,
	fireAnalyticsEvent,
	isDraggableItem,
	performDropOperation,
}: {
	canDrop: (data: T) => data is T;
	fireAnalyticsEvent?: AnalyticsEventHandler;
	isDraggableItem: (data: T) => boolean;
	performDropOperation: (
		operation: MenuItemReorderOperation,
		dragging: T,
		dropTargetData: T,
	) => void;
}): DropTarget<T> {
	const ref = useRef<HTMLDivElement | null>(null);
	const [state, setState] = useState<'idle' | 'is-over'>('idle');

	useEffect(() => {
		const element = ref.current;

		// might have no items
		if (!element) {
			return;
		}

		return dropTargetForElements({
			element,
			canDrop: ({ source }) => {
				const data = source.data as T;
				return canDrop(data);
			},
			onDragStart({ source }) {
				// Set is-over state when drag starts from within this group
				// This handles the case where drag begins inside the drop zone
				const data = source.data as T;
				if (isDraggableItem(data)) {
					setState('is-over');
				}

				fireAnalytics(fireAnalyticsEvent, 'dragStarted', data.menuItemId);
			},
			onDragEnter({ source }) {
				setState('is-over');
				const data = source.data as T;
				fireAnalytics(fireAnalyticsEvent, 'dragEntered', data.menuItemId);
			},
			onDragLeave({ source }) {
				setState('idle');
				const data = source.data as T;
				fireAnalytics(fireAnalyticsEvent, 'dragLeft', data.menuItemId);
			},
			onDrop({ source, location }) {
				setState('idle');
				const dragging = source.data as T;
				const [innerMost] = location.current.dropTargets;
				const dropTargetData = innerMost.data as T;

				if (!isDraggableItem(dragging) || !isDraggableItem(dropTargetData)) {
					fireAnalytics(fireAnalyticsEvent, 'dragEnded', dragging.menuItemId, undefined, false);
					return;
				}

				// Extract instruction from dropTargetData that has instruction property
				const instruction = extractInstruction(dropTargetData);
				const operation = instruction && instructionToOperation(instruction);

				if (operation) {
					performDropOperation(operation, dragging, dropTargetData);
				}

				if (source.element) {
					triggerPostMoveFlash(source.element);
				}

				fireAnalytics(
					fireAnalyticsEvent,
					'dragEnded',
					dragging.menuItemId,
					operation || undefined,
					true,
				);
			},
		});
	}, [canDrop, fireAnalyticsEvent, isDraggableItem, performDropOperation]);

	return { ref, state };
}

const instructionToOperation = (instruction: Instruction): MenuItemReorderOperation | null => {
	switch (instruction?.operation) {
		case 'reorder-before':
			return OPERATIONS.BEFORE;
		case 'reorder-after':
			return OPERATIONS.AFTER;
		default:
			return null;
	}
};

export const fireAnalytics = (
	fireAnalyticsEvent: AnalyticsEventHandler | undefined,
	action: 'dragStarted' | 'dragEnded' | 'dragEntered' | 'dragLeft',
	itemId: string,
	operation?: MenuItemReorderOperation,
	successfulReorder?: boolean,
) => {
	if (fireAnalyticsEvent) {
		fireAnalyticsEvent({
			action,
			itemId,
			operation,
			successfulReorder,
		});
	}
};
